import type { UptimeDay } from '../api/statusApi';
import './UptimeHistory.css';

interface Props {
  days: UptimeDay[];
}

const WINDOW_DAYS = 90;

function barColor(pct: number): string {
  if (pct >= 99.9) return 'var(--status-operational)';
  if (pct >= 99) return 'var(--status-degraded)';
  if (pct >= 95) return 'var(--status-partial)';
  return 'var(--status-major)';
}

/** The last WINDOW_DAYS UTC dates, oldest first, ending today (uptime.json dates are UTC). */
function windowDates(): string[] {
  const today = new Date();
  const out: string[] = [];
  for (let n = WINDOW_DAYS - 1; n >= 0; n--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - n));
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

function formatDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// uptime.json only has days something observed the component. Every other day in the
// window is drawn grey as "no data" — never as uptime nobody measured.
export function UptimeHistory({ days }: Props) {
  const byDate = new Map(days.map((d) => [d.date, d.uptime_pct]));
  const dates = windowDates();
  const observed = dates.filter((d) => byDate.has(d));
  const avg =
    observed.length > 0 ? observed.reduce((sum, d) => sum + (byDate.get(d) ?? 0), 0) / observed.length : undefined;

  return (
    <div className="uptime-history">
      <div
        className="uptime-history__bars"
        role="img"
        aria-label={
          observed.length > 0
            ? `Uptime for the ${observed.length} observed days of the last ${WINDOW_DAYS}`
            : `No uptime data yet for the last ${WINDOW_DAYS} days`
        }
      >
        {dates.map((date) => {
          const pct = byDate.get(date);
          return (
            <span
              key={date}
              className="uptime-history__bar"
              style={{ background: pct === undefined ? 'var(--uptime-no-data)' : barColor(pct) }}
              title={pct === undefined ? `${date}: no data` : `${date}: ${pct.toFixed(2)}% uptime`}
            />
          );
        })}
      </div>
      <div className="uptime-history__footer">
        <span>{observed.length > 0 ? `Monitored since ${formatDay(observed[0])}` : 'No data yet'}</span>
        {avg !== undefined && <span>{avg.toFixed(2)}% avg uptime</span>}
      </div>
    </div>
  );
}
